import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { RefreshCw, X } from "lucide-react";

/**
 * Listens for the `hibane:sw-update-available` window event dispatched by
 * `swUpdater.ts` when a genuinely new service worker (different BUILD_ID) has
 * finished installing. Renders a bottom-right toast that lets the user choose
 * when to activate the update; activation triggers a single page reload via
 * the controllerchange handler in swUpdater.
 *
 * Mounted once at the application root (next to <Toaster />).
 */
type Activate = () => void;

export default function SWUpdatePrompt() {
  const [activate, setActivate] = useState<Activate | null>(null);
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<{ activate: Activate }>).detail;
      if (!detail || typeof detail.activate !== "function") return;
      setActivate(() => detail.activate);
      setDismissed(false);
    };
    window.addEventListener("hibane:sw-update-available", handler);
    return () => window.removeEventListener("hibane:sw-update-available", handler);
  }, []);

  if (!activate || dismissed) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-4 right-4 z-[100] w-[min(22rem,calc(100vw-2rem))] rounded-card border border-border/40 bg-surface shadow-card p-4 flex items-start gap-3 animate-in slide-in-from-bottom-2 fade-in"
    >
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-ink">New version available</p>
        <p className="text-xs text-ink-muted mt-0.5">
          Refresh to load the latest improvements.
        </p>
        <div className="flex items-center gap-2 mt-3">
          <Button
            variant="fintech"
            size="sm"
            onClick={() => activate()}
            className="gap-1.5"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </Button>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setDismissed(true)}
            className="text-ink-muted"
          >
            Later
          </Button>
        </div>
      </div>
      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label="Dismiss"
        className="text-ink-muted hover:text-ink rounded-full p-1 -mt-1 -mr-1"
      >
        <X className="h-4 w-4" />
      </button>
    </div>
  );
}